import axiosInstance from '../axiosInstance';

/**
 * Documents centre API (SUNSKY Documents spec v1.2).
 * Backend: backend/routes/document/documentCentre.routes.js
 */

const BASE = '/documents/centre';

// Sending renders and attaches PDFs, and a print file merges them, so these
// calls get far more time than the shared 10 second default.
const LONG = 180000;

const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length))
      .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v])
  );

/** The API's own message from an axios error, including JSON sent back as a Blob. */
export const errorMessage = async (error, fallback = 'Something went wrong') => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || error?.message || fallback;
};

export const fetchCentreList = async (params) => {
  const { data } = await axiosInstance.get(`${BASE}/list`, { params: clean(params), timeout: 60000 });
  return data;
};

export const fetchCentreIds = async (params) => {
  const { data } = await axiosInstance.get(`${BASE}/ids`, { params: clean(params), timeout: 60000 });
  return data.data;
};

export const reviewDispatch = async (body) => {
  const { data } = await axiosInstance.post(`${BASE}/review`, body, { timeout: 60000 });
  return data.data;
};

export const executeDispatch = async (body) => {
  const { data } = await axiosInstance.post(`${BASE}/execute`, body, { timeout: LONG });
  return data.data;
};

export const fetchCentreFile = async (token) => {
  const response = await axiosInstance.get(`${BASE}/files/${token}`, { responseType: 'blob', timeout: LONG });
  return response.data;
};

export const retryDispatches = async (dispatchIds) => {
  const { data } = await axiosInstance.post(`${BASE}/retry`, { dispatchIds }, { timeout: LONG });
  return data.data;
};

export const fetchDispatches = async (params) => {
  const { data } = await axiosInstance.get(`${BASE}/dispatches`, { params: clean(params), timeout: 60000 });
  return data;
};

export const fetchDispatchOptions = async () => {
  const { data } = await axiosInstance.get(`${BASE}/dispatches/options`);
  return data.data;
};

export const resolveDispatch = async (id, note) => {
  const { data } = await axiosInstance.post(`${BASE}/dispatches/${id}/resolve`, { note });
  return data;
};

export const fetchDocumentHistory = async (id) => {
  const { data } = await axiosInstance.get(`${BASE}/documents/${id}/history`, { timeout: 60000 });
  return data.data;
};

/** purpose: view (marked when not released) | internal | customer */
export const fetchDocumentPdf = async (id, purpose = 'view') => {
  const response = await axiosInstance.get(`${BASE}/documents/${id}/pdf`, {
    params: { purpose },
    responseType: 'blob',
    timeout: LONG,
  });
  return response.data;
};

export const fetchVersionPdf = async (versionId) => {
  const response = await axiosInstance.get(`${BASE}/versions/${versionId}/pdf`, { responseType: 'blob', timeout: LONG });
  return response.data;
};

export const fetchReleaseExceptions = async (bookingReference) => {
  const { data } = await axiosInstance.get(`${BASE}/release-exceptions`, { params: { bookingReference } });
  return data.data;
};

export const grantReleaseException = async (body) => {
  const { data } = await axiosInstance.post(`${BASE}/release-exceptions`, body);
  return data;
};

export const revokeReleaseException = async (id, reason) => {
  const { data } = await axiosInstance.post(`${BASE}/release-exceptions/${id}/revoke`, { reason });
  return data;
};

export const fetchMissingDocuments = async (params) => {
  const { data } = await axiosInstance.get(`${BASE}/missing`, { params: clean(params), timeout: 60000 });
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// Opening and saving files
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A window opened synchronously inside the click handler, so the browser's
 * popup blocker allows it; the PDF is loaded into it once it has arrived.
 */
export const openPendingWindow = () => {
  const win = window.open('', '_blank');
  if (win) {
    try {
      win.document.write('<p style="font: 14px sans-serif; padding: 24px; color: #475569">Preparing the document...</p>');
    } catch {
      /* cross-origin about:blank on some browsers; nothing to write */
    }
  }
  return win;
};

export const showBlobIn = (win, blob, type = 'application/pdf') => {
  const url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type }));
  if (win && !win.closed) win.location.href = url;
  else window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
};

export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
};
